import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Form, Input, message, Radio } from "antd";
import { captureException } from "@sentry/nextjs";
import moment from "moment";
import { HiOutlineCamera, HiOutlineClock, HiOutlineLocationMarker, HiOutlineLockClosed } from "react-icons/hi";
import InfyButton from "../../../InfyButton/InfyButton";
import { GeoCaptureContext, getGeoCaptureContext, submitLearnerResponse } from "../../apis";
import { buildResponses } from "../../formUtils";
import getQuestionComponent from "../../getQuestionComponent";
import { ATTENDANCE_ANSWERS, QUESTION_TYPES } from "../../constants";
import { resolveSubmitErrorMessage, V2FormData, V2Question } from "../../helper";
import AutoCloseNotice from "../AutoCloseNotice/AutoCloseNotice";
import TenantLogo from "../TenantLogo/TenantLogo";
import useTenantLogo from "../TenantLogo/useTenantLogo";
import FormLabel from "../FormLabel";
import { GEOLOCATION_OPTIONS } from "../GeoCameraModal";
import GeoCaptureCompleted from "./GeoCaptureCompleted";
import PermissionBlockedNotice from "./PermissionBlockedNotice";
import { probeCamera, probeLocation } from "./accessProbe";
import { isTurnedOffBySitePolicy } from "../../sitePermissionPolicy";
import { DEFAULT_OFFICE_RADIUS_METERS, distanceFromOfficeMeters, formatDistance } from "./officeDistance";
import styles from "./GeoCaptureForm.module.scss";

interface GeoCaptureFormProps {
  formData: V2FormData;
  formId: string;
  parameters: Record<string, string>;
}

const ABSENCE_REMARK_MAX = 1000;
const LOCATION_REQUIRED_MESSAGE = "Location access is required to submit this form";

interface AbsentCapture {
  latitude: number;
  longitude: number;
  accuracy: number | null;
  capturedAt: string;
}

/** "locating" until a read settles; "failed" covers denied, unavailable and timed out alike (all fixed by Try again). */
type AbsentLocationState = "locating" | "ready" | "failed";

type AccessState = "granted" | "denied" | "prompt" | "unknown";

type WorkLocation = "OFFICE" | "HOME";

const WORK_LOCATION_REQUIRED_MESSAGE = "Tell us where you are working today";
/** The server's refusal when a Hybrid Present submission carries no work location (400). */
const WORK_LOCATION_SERVER_MESSAGE = "Tell us whether you are working from the office or from home";
const GEO_CONTEXT_RETRY_MS = 1500;
/** setTimeout stores its delay as a signed 32-bit number; a longer one fires at once. */
const MAX_TIMER_MS = 2 ** 31 - 1;
const WINDOW_TIME_FORMAT = "h:mm A, D MMM";

type WindowState = "upcoming" | "open" | "closed";

/**
 * Reads a permission without assuming the browser supports asking about it. Safari and Firefox
 * reject or omit the "camera" descriptor, and a rejected query must read as "unknown" rather than
 * as a denial — otherwise the form would warn every Safari user that they cannot complete it.
 */
/** Returned when there is nothing to unsubscribe from, so every caller can clean up unconditionally. */
const noUnsubscribe = () => undefined;

const readPermission = async (name: PermissionName, onChange: (state: AccessState) => void): Promise<() => void> => {
  try {
    if (!navigator.permissions?.query) {
      onChange("unknown");
      return noUnsubscribe;
    }
    const status = await navigator.permissions.query({ name });
    onChange(status.state as AccessState);
    // A learner who fixes the permission in site settings, or blocks it at the browser prompt, must
    // see the page change without reloading — a one-shot read on mount goes stale the moment they act.
    const handler = () => onChange(status.state as AccessState);
    status.addEventListener("change", handler);
    return () => status.removeEventListener("change", handler);
  } catch {
    onChange("unknown");
    return noUnsubscribe;
  }
};

/** A capture counts as answered only with coordinates — a URL on its own is not a geo capture. */
const hasLocatedPhoto = (value: unknown): boolean => {
  const capture = value as { url?: string; latitude?: number; longitude?: number } | undefined;
  return Boolean(capture?.url) && typeof capture?.latitude === "number" && typeof capture?.longitude === "number";
};

/**
 * Dedicated layout for GEO_CAPTURE forms, selected on the form's type the same way COURSE_FEEDBACK
 * and job-form surveys are.
 *
 * It exists for one reason the generic layout cannot serve: these questions need the camera and the
 * device location, and in the generic form a learner only discovers a denied permission *inside* the
 * capture modal — after committing to the action, with no explanation and no way to recover. Here
 * both are checked on arrival and the blocking case is stated up front.
 *
 * The photo leads: it is the point of the form, and any text questions are follow-ups to it.
 *
 * Submission deliberately reuses `buildResponses` and `submitLearnerResponse` rather than
 * assembling its own payload, so the GEO_PICTURE answer split and the single/multiple-submission
 * rules behave identically to every other form.
 */
const GeoCaptureForm: React.FC<GeoCaptureFormProps> = ({ formData, formId, parameters }) => {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [cameraAccess, setCameraAccess] = useState<AccessState>("unknown");
  const [locationAccess, setLocationAccess] = useState<AccessState>("unknown");
  // The tenant logo, above the card and on the result screen as the job form shows it ("" = none).
  const logo = useTenantLogo();

  const questions: V2Question[] = useMemo(() => [...(formData?.questions || [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)), [formData]);

  // The capture questions lead regardless of the order they were authored in; everything else keeps
  // its relative order underneath.
  /**
   * The gate is found by its `metadata.isAttendanceGate` flag, never by label or position — admins
   * can reword and reorder the question, and translations must keep working.
   */
  const gateQuestion = useMemo(() => questions.find(question => (question.metadata as { isAttendanceGate?: boolean })?.isAttendanceGate === true), [questions]);
  const captureQuestions = useMemo(() => questions.filter(question => question.questionType === QUESTION_TYPES.GEO_PICTURE), [questions]);
  // Found by its marker, never by label or position. Only an older form, or one never re-saved, lacks it.
  const remarkQuestion = useMemo(() => questions.find(question => (question.metadata as { isAbsenceRemark?: boolean })?.isAbsenceRemark === true), [questions]);
  const followUpQuestions = useMemo(
    () =>
      questions.filter(
        question => question.questionType !== QUESTION_TYPES.GEO_PICTURE && question.id !== gateQuestion?.id && question.id !== remarkQuestion?.id
      ),
    [questions, gateQuestion, remarkQuestion]
  );

  const attendance = Form.useWatch(gateQuestion?.id, form);
  // No gate on the form (an older one, or one built through the API) means no gating — show the
  // capture rather than hiding it behind a question that does not exist.
  const showCapture = !gateQuestion || attendance === ATTENDANCE_ANSWERS.PRESENT;
  const isAbsent = Boolean(gateQuestion) && attendance === ATTENDANCE_ANSWERS.ABSENT;
  const isPresent = Boolean(gateQuestion) && attendance === ATTENDANCE_ANSWERS.PRESENT;

  // The office check's settings for this request (Rev 69). A failed read leaves it null: no work-location choice and no
  // status banner, and the form works as before. The server decides either way.
  const relatedModelId = parameters?.relatedModelId || parameters?.relatedModelid;
  const [geoContext, setGeoContext] = useState<GeoCaptureContext | null>(null);
  const [clockOffset, setClockOffset] = useState(0);
  // upcoming / open / closed against the request's window, on the server's clock. null (no window sent, or the read
  // failed) means no banner and no lock: the server's submit check stays the guard.
  const [windowState, setWindowState] = useState<WindowState | null>(null);
  const opensAtMs = Date.parse(String(geoContext?.opensAt ?? ""));
  const closesAtMs = geoContext?.closesAt ? Date.parse(geoContext.closesAt) : Number.NaN;
  useEffect(() => {
    if (Number.isNaN(opensAtMs)) {
      setWindowState(null);
      return undefined;
    }
    let timer: ReturnType<typeof setTimeout> | undefined;
    const evaluate = () => {
      const now = Date.now() + clockOffset;
      let next: number | null = null;
      if (now < opensAtMs) {
        setWindowState("upcoming");
        next = opensAtMs;
      } else if (!Number.isNaN(closesAtMs) && now > closesAtMs) {
        setWindowState("closed");
      } else {
        setWindowState("open");
        if (!Number.isNaN(closesAtMs)) next = closesAtMs;
      }
      // One timer for the next boundary, not a poll. A boundary beyond the setTimeout limit is capped and re-checked.
      if (next !== null) timer = setTimeout(evaluate, Math.min(Math.max(next - now, 0) + 50, MAX_TIMER_MS));
    };
    evaluate();
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [opensAtMs, closesAtMs, clockOffset]);
  const windowNotice = (() => {
    if (!windowState) return null;
    const opensText = moment(opensAtMs).format(WINDOW_TIME_FORMAT);
    const closesText = Number.isNaN(closesAtMs) ? "" : moment(closesAtMs).format(WINDOW_TIME_FORMAT);
    if (windowState === "upcoming") return { tone: "bg-yellow-50 text-yellow-700", lock: true, text: `Opens at ${opensText}. You can submit from then.` };
    if (windowState === "closed") return { tone: "bg-red-50 text-red-700", lock: true, text: `This capture closed at ${closesText}.` };
    return closesText ? { tone: "bg-green-50 text-green-700", lock: false, text: `Open until ${closesText}.` } : null;
  })();
  const windowLocked = windowState === "upcoming" || windowState === "closed";
  useEffect(() => {
    if (!relatedModelId) return undefined;
    let active = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const load = (retry: boolean) => {
      getGeoCaptureContext(relatedModelId)
        .then(context => {
          if (!active || !context || typeof context !== "object") return;
          // Device clock minus server clock, taken when the answer arrives; 0 when the server sent no clock.
          const serverTime = Date.parse(String(context.serverNow ?? ""));
          setClockOffset(Number.isNaN(serverTime) ? 0 : serverTime - Date.now());
          setGeoContext(context);
        })
        .catch(() => {
          // One retry, so a blip does not strand a Hybrid learner without the work-location choice.
          if (active && retry) timer = setTimeout(() => load(false), GEO_CONTEXT_RETRY_MS);
        });
    };
    load(true);
    return () => {
      active = false;
      if (timer) clearTimeout(timer);
    };
  }, [relatedModelId]);
  const [workLocation, setWorkLocation] = useState<WorkLocation | null>(null);
  // Set when the server refuses a submit for a missing work location: the context call failed, but the learner can still answer.
  const [workLocationRefused, setWorkLocationRefused] = useState(false);
  const isHybrid = Boolean(geoContext?.distanceCheck) && String(geoContext?.mode ?? "").toUpperCase() === "HYBRID";
  const askWorkLocation = (isHybrid || workLocationRefused) && isPresent;

  // Location is required for an Absent learner too: Submit stays disabled until a position is read. No photo.
  const [absentCapture, setAbsentCapture] = useState<AbsentCapture | null>(null);
  const [absentLocationState, setAbsentLocationState] = useState<AbsentLocationState>("locating");
  const absentRead = useRef(0);
  const readAbsentLocation = useCallback(() => {
    const token = ++absentRead.current;
    setAbsentCapture(null);
    setAbsentLocationState("locating");
    const fail = () => {
      if (absentRead.current === token) setAbsentLocationState("failed");
    };
    try {
      if (!navigator.geolocation) {
        fail();
        return;
      }
      navigator.geolocation.getCurrentPosition(
        position => {
          if (absentRead.current !== token) return;
          setAbsentCapture({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy ?? null,
            capturedAt: new Date().toISOString()
          });
          setAbsentLocationState("ready");
        },
        fail,
        GEOLOCATION_OPTIONS
      );
    } catch {
      fail();
    }
  }, []);
  useEffect(() => {
    if (isAbsent) {
      readAbsentLocation();
    } else {
      // Present (or nothing chosen): drop the position and ignore any read still in flight.
      absentRead.current += 1;
      setAbsentCapture(null);
      setAbsentLocationState("locating");
    }
  }, [isAbsent, readAbsentLocation]);

  useEffect(() => {
    let active = true;
    const guard = (setter: (state: AccessState) => void) => (state: AccessState) => {
      if (active) setter(state);
    };

    const cleanups = Promise.all([
      readPermission("camera" as PermissionName, guard(setCameraAccess)),
      readPermission("geolocation" as PermissionName, guard(setLocationAccess))
    ]);

    return () => {
      active = false;
      cleanups.then(fns => fns.forEach(fn => fn()));
    };
  }, []);

  // A learner who allows location in the browser settings while the error is showing should not have to press Try again.
  // Fires only on the transition INTO "granted". Keying it on the failed state would loop forever when
  // permission is granted but the read keeps failing (OS location off, no fix); Try again covers that case.
  const previousLocationAccess = useRef<AccessState | null>(null);
  useEffect(() => {
    const wasGranted = previousLocationAccess.current === "granted";
    previousLocationAccess.current = locationAccess;
    if (isAbsent && locationAccess === "granted" && !wasGranted) readAbsentLocation();
  }, [isAbsent, locationAccess, readAbsentLocation]);

  // Only "denied" blocks. Safari and Firefox reject or omit these descriptors and read as "unknown",
  // which must stay passable — the real guarantee is at capture time, where a photo cannot be saved
  // without coordinates whatever the pre-flight said.
  // An Absent learner takes no photo, so the camera never blocks them; their location still does.
  const blockedOn = [!isAbsent && cameraAccess === "denied" && "camera", locationAccess === "denied" && "location"].filter(Boolean) as string[];

  // "Check again": re-tests what is blocked with a real camera / location read (the browser cannot always say), and clears the
  // block for whatever now works. A location that comes back granted also restarts an Absent learner's read, through the effect above.
  const [checking, setChecking] = useState(false);
  const [stillBlocked, setStillBlocked] = useState(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const checkAgain = async () => {
    setChecking(true);
    const [camera, location] = await Promise.all([
      !isAbsent && cameraAccess === "denied" ? probeCamera() : Promise.resolve("granted" as const),
      locationAccess === "denied" ? probeLocation() : Promise.resolve("granted" as const)
    ]);
    if (!mounted.current) return;
    if (camera === "granted") setCameraAccess(current => (current === "denied" ? "granted" : current));
    if (location === "granted") setLocationAccess(current => (current === "denied" ? "granted" : current));
    setStillBlocked(camera !== "granted" || location !== "granted");
    setChecking(false);
  };
  // A block that clears and later returns starts without the "still blocked" note.
  useEffect(() => {
    if (blockedOn.length === 0) setStillBlocked(false);
  }, [blockedOn.length]);
  // What the site's own policy turns off (Chrome / Edge can tell): no setting on the learner's device can allow it, so the notice
  // says so instead of showing steps that cannot help.
  const siteOffOn = blockedOn.filter(name => isTurnedOffBySitePolicy(name === "location" ? "geolocation" : "camera"));
  const siteOffTitle = `${siteOffOn.map((name, index) => (index === 0 ? name.charAt(0).toUpperCase() + name.slice(1) : name)).join(" and ")} ${
    siteOffOn.length > 1 ? "are" : "is"
  } turned off for this site`;
  const blockedTitle = `${blockedOn.map((name, index) => (index === 0 ? name.charAt(0).toUpperCase() + name.slice(1) : name)).join(" and ")} ${
    blockedOn.length > 1 ? "are" : "is"
  } blocked`;

  // The whole store, not a selector: `useWatch` in rc-field-form 1.x takes a name path only, and a
  // function passed here is read as a field name — which would leave Submit disabled forever.
  const formValues = Form.useWatch([], form) as Record<string, unknown> | undefined;
  /**
   * Submission is held until every capture has a photo *and* its coordinates, and only for a learner
   * who has marked themselves present. Someone who answered absent has no photo to take and must
   * still be able to submit.
   *
   * This does not rely on the question being marked required: an admin who unticks that would
   * otherwise let a present learner submit with no capture at all, which is the one thing this form
   * type exists to prevent.
   */
  // An Absent learner must have a position too (the form is submitted with where they were, not a photo).
  const absentLocationMissing = isAbsent && !absentCapture;
  const workLocationMissing = askWorkLocation && !workLocation;
  const capturesMissing = showCapture && captureQuestions.length > 0 && !captureQuestions.every(question => hasLocatedPhoto(formValues?.[question.id]));

  const handleFinish = async (values: Record<string, unknown>) => {
    // Belt and braces. The button is disabled, but a browser can still submit a form implicitly from
    // a text field, and this form must never record a present learner with no located photo.
    if (submitting || capturesMissing) return;
    if (windowLocked && windowNotice) {
      message.info(windowNotice.text);
      return;
    }
    if (workLocationMissing) {
      message.error(WORK_LOCATION_REQUIRED_MESSAGE);
      return;
    }
    if (isAbsent && !absentCapture) {
      message.error(LOCATION_REQUIRED_MESSAGE);
      return;
    }
    setSubmitting(true);
    try {
      // The remark is only part of an Absent submission; Present drops it.
      const submitQuestions = remarkQuestion && !isAbsent ? questions.filter(question => question.id !== remarkQuestion.id) : questions;
      const submitValues = remarkQuestion && isAbsent ? { ...values, [remarkQuestion.id]: String(values[remarkQuestion.id] ?? "").trim() } : values;
      const responses = buildResponses(submitQuestions, submitValues).map(response => {
        if (response.questionId !== gateQuestion?.id) return response;
        if (isAbsent && absentCapture) return { ...response, otherFields: { capture: absentCapture } };
        // Present in a Hybrid internship: where the learner works today rides on the gate answer.
        if (askWorkLocation && workLocation) return { ...response, otherFields: { ...(response.otherFields || {}), workLocation } };
        return response;
      });
      await submitLearnerResponse(formId, {
        responses,
        componentId: parameters?.componentId,
        courseId: parameters?.courseId || null,
        // Without these the submission is stored but never bound to the ask that requested it: the
        // target stays PENDING, and the course report shows the capture as still open forever.
        //
        // Both spellings are read because the link core-api generates carries them lowercased
        // (`relatedModelname`, `relatedModelid`) while every other caller in this app uses camelCase,
        // and nothing normalises query keys between the two.
        relatedModelName: parameters?.relatedModelName || parameters?.relatedModelname,
        relatedModelId: parameters?.relatedModelId || parameters?.relatedModelid
      });
      setSubmitted(true);
      window?.parent?.postMessage({ action: "formSubmitted" }, "*");
    } catch (error) {
      captureException(error);
      // The learner must see why: a capture outside its window or a second one against the same ask is
      // refused with the server's own reason, and the photo is still on screen to try again.
      const reason = resolveSubmitErrorMessage(error);
      if (String(reason).includes(WORK_LOCATION_SERVER_MESSAGE)) setWorkLocationRefused(true);
      message.error(reason);
      window?.parent?.postMessage({ action: "somethingWentWrong" }, "*");
    } finally {
      setSubmitting(false);
    }
  };

  // The status banner under the photo (Rev 69): distance from the office, or a neutral "Location recorded". Never shows
  // coordinates or place names, and never blocks Submit; core-api decides Present / Absent from its own numbers.
  const capturedPhoto = (captureQuestions[0] ? formValues?.[captureQuestions[0].id] : undefined) as
    | { latitude?: number; longitude?: number; accuracy?: number | null }
    | undefined;
  const locationBanner = (() => {
    // distanceCheck off: no banner (GeoPicture already shows its own "Location recorded" line).
    // core-api applies the distance rule only to a Present answer, so no banner for anything else.
    if (!geoContext || !geoContext.distanceCheck || !isPresent || !showCapture || !hasLocatedPhoto(capturedPhoto)) return null;
    const mode = String(geoContext.mode ?? "").toUpperCase();
    const checked = geoContext.distanceCheck && geoContext.office && (mode === "OFFLINE" || (mode === "HYBRID" && workLocation === "OFFICE"));
    if (!checked || !geoContext.office || typeof capturedPhoto?.latitude !== "number" || typeof capturedPhoto?.longitude !== "number") {
      return { tone: "neutral" as const, text: "Location recorded" };
    }
    const distance = distanceFromOfficeMeters(
      { latitude: capturedPhoto.latitude, longitude: capturedPhoto.longitude, accuracy: capturedPhoto.accuracy },
      geoContext.office
    );
    if (distance <= (geoContext.radiusMeters ?? DEFAULT_OFFICE_RADIUS_METERS)) return { tone: "ok" as const, text: "You're at your office" };
    return { tone: "far" as const, text: `You're about ${formatDistance(distance)} from your office. This will be recorded as Absent.` };
  })();
  const bannerClass = { neutral: "bg-neutral-3 text-neutral-9", ok: "bg-green-50 text-green-700", far: "bg-red-50 text-red-700" };

  // Only a submission that just succeeded reaches this screen, so the tab may close itself from here.
  if (submitted) {
    return (
      <GeoCaptureCompleted logo={logo}>
        <AutoCloseNotice className="mt-6" />
      </GeoCaptureCompleted>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-2 px-4 py-6">
      {/* The card's column, so the logo lines up with the card's left edge. */}
      {logo && (
        <div className="mx-auto flex w-full max-w-xl flex-col">
          <TenantLogo src={logo} />
        </div>
      )}
      <div className="mx-auto flex w-full max-w-xl flex-col gap-5 rounded-2xl bg-white p-5 shadow-sm">
        <div className="flex flex-col gap-1">
          <h1 className="text-xl font-semibold text-neutral-13">{formData?.title}</h1>
          {formData?.description && <p className="text-sm text-neutral-9">{formData.description}</p>}
        </div>

        {blockedOn.length > 0 ? (
          <PermissionBlockedNotice
            title={siteOffOn.length > 0 ? siteOffTitle : blockedTitle}
            description={
              siteOffOn.length > 0
                ? "This isn't a setting on your phone or browser, so changing it there won't help. Please tell your placement team; you can't submit until it's turned back on."
                : isAbsent
                ? "You can't submit until you allow your location for this site."
                : "The photo is saved with where you were, so you can't submit until you allow them for this site."
            }
            onCheckAgain={checkAgain}
            checking={checking}
            stillBlocked={stillBlocked && siteOffOn.length === 0}
            showSteps={siteOffOn.length < blockedOn.length}
          />
        ) : isAbsent ? null : (
          <div className="flex flex-col gap-1 rounded-lg bg-neutral-3 p-3 text-xs text-neutral-9">
            <span className="inline-flex items-center gap-2">
              <HiOutlineCamera className="text-sm" /> The photo is taken with your camera — it cannot be chosen from your gallery.
            </span>
            <span className="inline-flex items-center gap-2">
              <HiOutlineLocationMarker className="text-sm" /> Your location is recorded at the moment you take it.
            </span>
          </div>
        )}

        {windowNotice && (
          <div role="status" className={`flex items-center gap-2 rounded-lg p-3 text-sm ${windowNotice.tone}`}>
            {windowNotice.lock ? <HiOutlineLockClosed className="flex-shrink-0 text-base" /> : <HiOutlineClock className="flex-shrink-0 text-base" />}
            <span>{windowNotice.text}</span>
          </div>
        )}

        <Form form={form} layout="vertical" onFinish={handleFinish} requiredMark={false} className={`flex flex-col ${styles.geoCaptureFields}`}>
          {gateQuestion && <div>{getQuestionComponent({ question: gateQuestion, formType: formData?.type })}</div>}

          {askWorkLocation && (
            <Form.Item
              label={<FormLabel label="Where are you working today?" required />}
              validateStatus={workLocationRefused && !workLocation ? "error" : undefined}
              help={workLocationRefused && !workLocation ? WORK_LOCATION_SERVER_MESSAGE : undefined}
            >
              <Radio.Group value={workLocation} onChange={event => setWorkLocation(event.target.value as WorkLocation)}>
                <Radio value="OFFICE">Office</Radio>
                <Radio value="HOME">Home</Radio>
              </Radio.Group>
            </Form.Item>
          )}

          {/* Unmounted rather than hidden: an unmounted Form.Item is not validated, so a required
              capture question cannot block a learner who has answered that they are not there. */}
          {showCapture && captureQuestions.map(question => <div key={question.id}>{getQuestionComponent({ question, formType: formData?.type })}</div>)}

          {locationBanner && (
            <div role="status" className={`mb-4 flex gap-2 rounded-lg p-3 text-sm ${bannerClass[locationBanner.tone]}`}>
              <HiOutlineLocationMarker className="mt-0.5 flex-shrink-0 text-base" />
              <span>{locationBanner.text}</span>
            </div>
          )}

          {isAbsent && remarkQuestion && (
            <Form.Item
              name={remarkQuestion.id}
              label={<FormLabel label={remarkQuestion.label} required description={remarkQuestion.description} />}
              rules={[
                { required: true, whitespace: true, message: "Please tell us why you are absent" },
                {
                  validator: (_rule, value) => {
                    const text = String(value ?? "").trim();
                    if (text.length > ABSENCE_REMARK_MAX) return Promise.reject(new Error(`Must be at most ${ABSENCE_REMARK_MAX} characters`));
                    if (/[<>]/.test(text)) return Promise.reject(new Error("The characters < and > are not allowed"));
                    return Promise.resolve();
                  }
                }
              ]}
            >
              <Input.TextArea placeholder={remarkQuestion.placeholder || "Enter"} autoSize={{ minRows: 3, maxRows: 5 }} />
            </Form.Item>
          )}

          {followUpQuestions.length > 0 && <div className="my-4 h-px w-full bg-neutral-4" />}

          {followUpQuestions.map(question => (
            <div key={question.id}>{getQuestionComponent({ question, formType: formData?.type })}</div>
          ))}

          {isAbsent && absentLocationState === "locating" && (
            <div className="mb-0 mt-2 flex gap-2 rounded-lg bg-neutral-3 p-3 text-xs text-neutral-9">
              <HiOutlineLocationMarker className="mt-0.5 flex-shrink-0 text-sm" />
              <span>Getting your location…</span>
            </div>
          )}

          {isAbsent && absentLocationState === "failed" && blockedOn.length === 0 && (
            <div className="mb-0 mt-2">
              <PermissionBlockedNotice
                title="We couldn't get your location"
                description={LOCATION_REQUIRED_MESSAGE}
                onCheckAgain={readAbsentLocation}
                checking={false}
                stillBlocked={false}
                showSteps={false}
                actionLabel="Try again"
              />
            </div>
          )}

          {capturesMissing && (
            <p className="mb-0 mt-2 text-center text-xs text-neutral-9">
              {locationAccess === "denied"
                ? "Allow location access for this site, then take the photo to submit."
                : "Take the photo to submit — it is saved with the location it was taken at."}
            </p>
          )}

          <InfyButton
            type="primary"
            bicolor="primary"
            htmlType="submit"
            className="mt-2 w-full"
            loading={submitting}
            disabled={submitting || windowLocked || capturesMissing || absentLocationMissing || workLocationMissing}
          >
            Submit
          </InfyButton>
        </Form>
      </div>
    </div>
  );
};

export default GeoCaptureForm;
